import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '@/lib/i18n';
import { useAuth } from '@/lib/auth';
import { usePlan } from '@/hooks/use-plan';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar } from '@/components/ui/calendar';
import { UpgradeBanner } from '@/components/shared/UpgradeBanner';
import { toast } from 'sonner';
import axios from 'axios';
import { format, startOfWeek, addDays, isBefore, startOfDay } from 'date-fns';
import { de as deLocale } from 'date-fns/locale';
import { ArrowLeft, Video, Clock, CalendarIcon, Check, X, Info, Mountain, CreditCard, Loader2, Compass } from 'lucide-react';
import { API } from '@/lib/api';

const TRAILS = [
  { id: 'vierwaldstaettersee', name: 'Vierwaldstättersee', tag: 'Strategie' },
  { id: 'greifensee', name: 'Greifensee', tag: 'Balance' },
  { id: 'aare_solothurn', name: 'Aare bei Solothurn', tag: 'Transformation' },
  { id: 'rigi_felsentor', name: 'Rigi – Felsentor', tag: 'Vision' },
  { id: 'flueli_ranft', name: 'Flüeli-Ranft', tag: 'Deep Dive' },
  { id: 'goetheanum', name: 'Goetheanum Dornach', tag: 'Kreation' },
  { id: 'heiligkreuz', name: 'Heiligkreuz Entlebuch', tag: 'Erdung' },
  { id: 'sils_maria', name: 'Sils-Maria & Chastè', tag: 'Souveränität' },
  { id: 'creux_du_van', name: 'Creux du Van', tag: 'Konfrontation' },
  { id: 'rheinau', name: 'Klosterinsel Rheinau', tag: 'Struktur' },
];

export default function BookingPage() {
  const { language } = useLanguage();
  const { user, getHeaders } = useAuth();
  const { isPremium } = usePlan();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const de = language === 'de';

  const [packages, setPackages] = useState({});
  const [selectedPkg, setSelectedPkg] = useState('premium_call');
  const [selectedDate, setSelectedDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedTrail, setSelectedTrail] = useState(null);
  const [myBookings, setMyBookings] = useState([]);
  const [bookedWeeks, setBookedWeeks] = useState({});
  const [booking, setBooking] = useState(false);
  const [polling, setPolling] = useState(false);

  useEffect(() => {
    fetchData();
    const sessionId = searchParams.get('session_id');
    const status = searchParams.get('status');
    if (sessionId && status === 'success') {
      pollPayment(sessionId);
    } else if (status === 'cancelled') {
      toast.error(de ? 'Zahlung abgebrochen' : 'Payment cancelled');
    }
  }, []);

  useEffect(() => { if (selectedDate) fetchSlots(format(selectedDate, 'yyyy-MM-dd')); }, [selectedDate]);

  const fetchData = async () => {
    const h = getHeaders();
    const [pkgRes, bRes, wRes] = await Promise.all([
      axios.get(`${API}/bookings/packages`).catch(() => ({ data: {} })),
      axios.get(`${API}/bookings/my`, { headers: h }).catch(() => ({ data: [] })),
      axios.get(`${API}/bookings/my-weeks`, { headers: h }).catch(() => ({ data: {} })),
    ]);
    setPackages(pkgRes.data || {});
    setMyBookings((bRes.data || []).filter(b => !isBefore(new Date(b.date + 'T23:59:59'), new Date())));
    setBookedWeeks(wRes.data || {});
  };

  const fetchSlots = async (date) => {
    try {
      const res = await axios.get(`${API}/bookings/available-slots?date=${date}`, { headers: getHeaders() });
      setSlots(res.data.slots || []);
    } catch { setSlots([]); }
  };

  const pollPayment = async (sessionId, attempt = 0) => {
    setPolling(true);
    try {
      const res = await axios.get(`${API}/bookings/payment-status/${sessionId}`, { headers: getHeaders() });
      if (res.data.payment_status === 'paid') {
        toast.success(de ? 'Zahlung erfolgreich! Buchung bestätigt.' : 'Payment successful! Booking confirmed.');
        setPolling(false);
        fetchData();
        return;
      }
      if (attempt < 5) setTimeout(() => pollPayment(sessionId, attempt + 1), 2000);
      else { setPolling(false); toast.info(de ? 'Zahlung wird verarbeitet...' : 'Payment processing...'); }
    } catch { setPolling(false); }
  };

  const pkg = packages[selectedPkg] || {};
  const isMotion = ['clarity_stride', 'focus_flow', 'resonanz_rallye', 'sovereign_vision_1', 'sovereign_vision_2', 'life_tag'].includes(selectedPkg);
  const needsPayment = pkg.price > 0;

  const getWeekKey = (date) => format(startOfWeek(date, { weekStartsOn: 1 }), 'yyyy-MM-dd');
  const selectedWeekBooking = selectedPkg === 'premium_call' && selectedDate ? bookedWeeks[getWeekKey(selectedDate)] : null;

  const handleBook = async () => {
    if (!selectedDate || !selectedSlot) return;
    setBooking(true);
    try {
      const res = await axios.post(`${API}/bookings/create`, {
        date: format(selectedDate, 'yyyy-MM-dd'),
        time: selectedSlot,
        package_id: selectedPkg,
        trail: selectedTrail,
        origin_url: window.location.origin,
      }, { headers: getHeaders() });
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        toast.success(de ? 'Termin gebucht!' : 'Appointment booked!');
        setSelectedDate(null); setSelectedSlot(null); setSelectedTrail(null);
        fetchData();
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
    setBooking(false);
  };

  const handleCancel = async (bookingId) => {
    try {
      await axios.delete(`${API}/bookings/${bookingId}`, { headers: getHeaders() });
      toast.success(de ? 'Storniert' : 'Cancelled');
      fetchData();
    } catch { toast.error('Error'); }
  };

  const FORMAT_GROUPS = [
    { label: de ? 'Premium-Abo' : 'Premium Subscription', items: [{ id: 'premium_call', premium: true }] },
    { label: 'Coaching in Motion', items: [{ id: 'clarity_stride' }, { id: 'focus_flow' }, { id: 'resonanz_rallye' }, { id: 'sovereign_vision_1' }, { id: 'sovereign_vision_2' }] },
    { label: 'belosophie LIFE', items: [{ id: 'life_tag' }] },
  ];

  return (
    <div className="space-y-6 pb-24 lg:pb-8 max-w-4xl" data-testid="booking-page">
      <div className="flex items-center gap-3">
        <button onClick={() => nav('/dashboard')} className="text-foreground/40 hover:text-foreground transition-colors"><ArrowLeft size={20} /></button>
        <div>
          <h1 className="font-display text-3xl font-medium tracking-tight">{de ? 'Termin buchen' : 'Book Appointment'}</h1>
          <p className="font-body text-sm text-foreground/50">{de ? 'Alle Formate – ein Kalender' : 'All formats – one calendar'}</p>
        </div>
      </div>

      {polling && (
        <div className="flex items-center gap-3 p-4 rounded-xl bg-green-50 border border-green-200" data-testid="payment-polling">
          <Loader2 size={18} className="text-green-600 animate-spin" />
          <p className="font-body text-sm text-green-700">{de ? 'Zahlung wird bestätigt...' : 'Confirming payment...'}</p>
        </div>
      )}

      {/* Format-Wahl */}
      <Card className="bg-white border border-border/60 rounded-2xl p-5" data-testid="format-selection">
        <h3 className="font-display text-base font-medium mb-4">{de ? '1. Format wählen' : '1. Choose Format'}</h3>
        <div className="space-y-4">
          {FORMAT_GROUPS.map((group, gi) => (
            <div key={gi}>
              <p className="font-body text-xs font-semibold text-foreground/40 uppercase tracking-wider mb-2">{group.label}</p>
              <div className="flex flex-wrap gap-2">
                {group.items.map(item => {
                  const p = packages[item.id] || {};
                  const active = selectedPkg === item.id;
                  const locked = item.premium && !isPremium;
                  return (
                    <button key={item.id} onClick={() => { if (!locked) { setSelectedPkg(item.id); setSelectedSlot(null); setSelectedTrail(null); } }}
                      className={`px-4 py-2 rounded-full font-body text-sm font-medium border transition-all ${active ? 'bg-terracotta text-white border-terracotta' : locked ? 'bg-warmgray/30 text-foreground/30 border-border/30' : 'bg-white text-foreground/70 border-border/60 hover:border-terracotta/50'}`}
                      data-testid={`pkg-${item.id}`}>
                      {p.name || item.id}
                      {p.price > 0 && <span className="ml-1.5 text-xs opacity-70">CHF {p.price}</span>}
                      {locked && <span className="ml-1 text-xs">🔒</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Trail-Wahl (nur bei Motion-Formaten) */}
      {isMotion && (
        <Card className="bg-white border border-border/60 rounded-2xl p-5" data-testid="trail-selection">
          <h3 className="font-display text-base font-medium mb-3 flex items-center gap-2">
            <Compass size={16} className="text-sage" /> {de ? '2. Signature Trail wählen (optional)' : '2. Choose Signature Trail (optional)'}
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            {TRAILS.map(trail => (
              <button key={trail.id} onClick={() => setSelectedTrail(selectedTrail === trail.id ? null : trail.id)}
                className={`p-2 rounded-xl font-body text-xs text-left border transition-all ${selectedTrail === trail.id ? 'bg-sage text-white border-sage' : 'bg-warmgray/20 text-foreground/60 border-border/30 hover:border-sage/50'}`}
                data-testid={`trail-${trail.id}`}>
                <p className="font-medium truncate">{trail.name}</p>
                <p className={`text-[9px] mt-0.5 ${selectedTrail === trail.id ? 'text-white/60' : 'text-foreground/30'}`}>{trail.tag}</p>
              </button>
            ))}
          </div>
        </Card>
      )}

      <div className="grid md:grid-cols-2 gap-6">
        {/* Kalender */}
        <Card className="bg-white border border-border/60 rounded-2xl p-5" data-testid="booking-calendar">
          <h3 className="font-display text-base font-medium mb-3 flex items-center gap-2">
            <CalendarIcon size={16} className="text-terracotta" />
            {de ? (isMotion ? '3. Datum wählen' : '2. Datum wählen') : (isMotion ? '3. Choose Date' : '2. Choose Date')}
          </h3>
          <Calendar mode="single" selected={selectedDate} onSelect={d => { setSelectedDate(d); setSelectedSlot(null); }}
            locale={de ? deLocale : undefined}
            disabled={(date) => isBefore(startOfDay(date), startOfDay(new Date()))}
            className="rounded-xl" />
        </Card>

        {/* Zeitslots */}
        <Card className="bg-white border border-border/60 rounded-2xl p-5" data-testid="booking-slots">
          <h3 className="font-display text-base font-medium mb-3 flex items-center gap-2">
            <Clock size={16} className="text-terracotta" />
            {de ? 'Uhrzeit wählen' : 'Choose Time'}
          </h3>
          {!selectedDate ? (
            <p className="font-body text-sm text-foreground/40 py-8 text-center">{de ? 'Bitte wähle ein Datum' : 'Please choose a date'}</p>
          ) : selectedWeekBooking ? (
            <div className="py-6 text-center space-y-2">
              <Check size={24} className="text-amber-600 mx-auto" />
              <p className="font-body text-sm font-medium">{de ? 'Diese Woche bereits ein Premium-Call' : 'Already a premium call this week'}</p>
              <p className="font-body text-xs text-foreground/40">{selectedWeekBooking.date} – {selectedWeekBooking.time}</p>
            </div>
          ) : slots.length === 0 ? (
            <p className="font-body text-sm text-foreground/40 py-8 text-center">{de ? 'Keine Slots verfügbar' : 'No slots available'}</p>
          ) : (
            <div className="space-y-3">
              <div>
                <p className="font-body text-[10px] font-semibold text-foreground/40 mb-1.5">{de ? 'Vormittag' : 'Morning'}</p>
                <div className="grid grid-cols-4 gap-1.5">
                  {slots.filter(s => parseInt(s) < 13).map(slot => (
                    <button key={slot} onClick={() => setSelectedSlot(slot)}
                      className={`p-2 rounded-lg font-body text-xs font-medium border transition-all ${selectedSlot === slot ? 'bg-terracotta text-white border-terracotta' : 'bg-warmgray/30 text-foreground/60 border-border/30 hover:border-terracotta/50'}`}
                      data-testid={`slot-${slot}`}>{slot}</button>
                  ))}
                </div>
              </div>
              {slots.some(s => parseInt(s) >= 13) && (
                <div>
                  <p className="font-body text-[10px] font-semibold text-foreground/40 mb-1.5">{de ? 'Nachmittag' : 'Afternoon'}</p>
                  <div className="grid grid-cols-4 gap-1.5">
                    {slots.filter(s => parseInt(s) >= 13).map(slot => (
                      <button key={slot} onClick={() => setSelectedSlot(slot)}
                        className={`p-2 rounded-lg font-body text-xs font-medium border transition-all ${selectedSlot === slot ? 'bg-terracotta text-white border-terracotta' : 'bg-warmgray/30 text-foreground/60 border-border/30 hover:border-terracotta/50'}`}
                        data-testid={`slot-${slot}`}>{slot}</button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Buchungsbestätigung */}
          {selectedDate && selectedSlot && !selectedWeekBooking && (
            <div className="mt-4 p-4 rounded-xl bg-terracotta/5 border border-terracotta/20">
              <p className="font-body text-sm font-medium text-foreground/80 mb-1">{pkg.name}</p>
              <p className="font-body text-xs text-foreground/50 mb-1">
                {format(selectedDate, de ? 'dd. MMMM yyyy' : 'MMMM dd, yyyy', { locale: de ? deLocale : undefined })} – {selectedSlot} Uhr · {pkg.duration}
              </p>
              {selectedTrail && <p className="font-body text-xs text-sage mb-1">Trail: {TRAILS.find(t => t.id === selectedTrail)?.name}</p>}
              {needsPayment && <p className="font-body text-sm font-semibold text-terracotta mb-2">CHF {pkg.price?.toFixed(0)} <span className="text-xs font-normal text-foreground/40">+ Spesen nach Aufwand</span></p>}
              <Button onClick={handleBook} disabled={booking}
                className="w-full rounded-full bg-terracotta hover:bg-terracotta-dark text-white py-4 font-medium shadow-lg shadow-terracotta/20"
                data-testid="confirm-booking-btn">
                {booking ? <Loader2 size={16} className="animate-spin" /> : needsPayment ? <><CreditCard size={16} className="mr-2" />{de ? 'Jetzt bezahlen' : 'Pay now'}</> : <><Video size={16} className="mr-2" />{de ? 'Termin buchen' : 'Book appointment'}</>}
              </Button>
            </div>
          )}
        </Card>
      </div>

      {/* Meine Termine */}
      {myBookings.length > 0 && (
        <Card className="bg-white border border-border/60 rounded-2xl p-5" data-testid="my-bookings">
          <h3 className="font-display text-base font-medium mb-3">{de ? 'Deine Termine' : 'Your Appointments'}</h3>
          <div className="space-y-2">
            {myBookings.map((b, i) => (
              <div key={i} className="flex items-center justify-between p-3 rounded-xl bg-warmgray/30" data-testid={`booking-${i}`}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-terracotta/10 flex items-center justify-center">
                    {b.package_id && b.package_id !== 'premium_call' ? <Mountain size={18} className="text-sage" /> : <Video size={18} className="text-terracotta" />}
                  </div>
                  <div>
                    <p className="font-body text-sm font-medium">{b.package_name || 'Video-Call'} – {b.date}</p>
                    <p className="font-body text-[10px] text-foreground/40">{b.time} Uhr · {b.duration || '15 Min'}{b.trail ? ` · ${b.trail}` : ''}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={`border-0 text-[9px] rounded-full ${b.status === 'confirmed' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                    {b.status === 'confirmed' ? (de ? 'Bestätigt' : 'Confirmed') : (de ? 'Zahlung ausstehend' : 'Payment pending')}
                  </Badge>
                  <button onClick={() => handleCancel(b.booking_id)} className="text-foreground/30 hover:text-red-500 transition-colors"><X size={16} /></button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
